import type { z } from 'zod'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { env, IS_DEV } from '@/lib/env'
import { TRACEPARENT_HEADER, newTraceparent } from '@/lib/traceparent'
import {
  AcknowledgementResponseSchema,
  AdminChannelSyncRunSchema,
  AdminChannelSyncStartedSchema,
  AmazonPoStatusReadResponseSchema,
  AuditTrailSchema,
  BulkWarehouseReleaseHistorySchema,
  BulkWarehouseReleaseResponseSchema,
  CancelSalesOrderResponseSchema,
  RefreshWarehouseStatusResponseSchema,
  ChannelsResponseSchema,
  FulfillableOrdersResponseSchema,
  MeSchema,
  MslListResponseSchema,
  MslUploadResultSchema,
  InventoryReportCatalogSchema,
  ItemsResponseSchema,
  OrderPageSchema,
  OrderSchema,
  OrderSummarySchema,
  PagedSalesOrderResponseSchema,
  ProblemSchema,
  SalesOrderDetailSchema,
  TenantsResponseSchema,
  ValidationRunSchema,
  VendorsResponseSchema,
  WarehouseReleaseResponseSchema,
  WarehousesResponseSchema,
  OrderInventorySchema,
  type AcknowledgementResponse,
  type AdminChannelSyncRun,
  type AdminChannelSyncStarted,
  type AdminChannelSyncType,
  type AmazonPoStatusReadResponse,
  type AuditTrail,
  type BulkWarehouseReleaseHistory,
  type BulkWarehouseReleaseRequest,
  type BulkWarehouseReleaseResponse,
  type CancelSalesOrderRequest,
  type CancelSalesOrderResponse,
  type RefreshWarehouseStatusResponse,
  type Me,
  type MslListResponse,
  type MslUploadResult,
  type BcSalesOrderFilter,
  type ChannelsResponse,
  type FulfillableOrdersResponse,
  type InventoryReportCatalog,
  type ItemsResponse,
  type Order,
  type OrderInventory,
  type OrderPage,
  type OrderStatus,
  type OrderSummary,
  type PagedSalesOrderResponse,
  type SalesOrderBcFilter,
  type SalesOrderDetail,
  type SalesOrderSort,
  type SalesOrderWarehouseFilter,
  type TenantsResponse,
  type ValidationRun,
  type VendorsResponse,
  type WarehouseReleaseLine,
  type WarehouseReleaseResponse,
  type WarehousesResponse,
} from '@/lib/vision/schemas'
import {
  OrderCancellationRequestSchema,
  OrderCancellationResponseSchema,
  type OrderCancellationErrorCode,
  type OrderCancellationRequest,
  type OrderCancellationResponse,
} from '@/lib/vision/order-cancellation-schema'
import {
  JitBulkSubmissionResultSchema,
  JitPurchaseOrderDetailSchema,
  JitPurchaseOrderPageSchema,
  JitSubmissionResultSchema,
  type JitBulkSubmissionRequest,
  type JitBulkSubmissionResult,
  type JitPurchaseOrderDetail,
  type JitPurchaseOrderPage,
  type JitSubmissionRequest,
  type JitSubmissionResult,
  type JitWorkItemStatus,
} from '@/lib/vision/jit-schemas'

/**
 * Thin fetch wrapper over Vision.
 *
 * Every call attaches `Authorization: Bearer <token>` from whichever token
 * source is active (MSAL silent → interactive; or the dev-JWT override), plus
 * `X-Tenant-Id` because Vision still reads tenant from a header (see
 * `OrdersQueryController.TENANT_HEADER` — the JWT-claim path lands later).
 *
 * Responses are Zod-parsed. In dev, a parse failure is logged loudly so that
 * a Vision contract drift is caught the moment it hits the browser instead of
 * three tabs later when a downstream `undefined.map` throws.
 */

export type TokenGetter = () => Promise<string>
export type TenantGetter = () => string

const CORRELATION_HEADER = 'X-Correlation-Id'
const TENANT_HEADER = 'X-Tenant-Id'
const IDEMPOTENCY_HEADER = 'Idempotency-Key'

export class VisionApiError extends Error {
  readonly status: number
  readonly problem: unknown
  readonly correlationId: string | undefined

  constructor(status: number, message: string, problem: unknown, correlationId?: string) {
    super(message)
    this.name = 'VisionApiError'
    this.status = status
    this.problem = problem
    this.correlationId = correlationId
  }
}

export class OrderCancellationApiError extends VisionApiError {
  readonly code: OrderCancellationErrorCode
  readonly serverCode: string | undefined

  constructor(error: VisionApiError) {
    super(error.status, error.message, error.problem, error.correlationId)
    this.name = 'OrderCancellationApiError'
    this.serverCode = problemCode(error.problem)
    this.code = cancellationErrorCode(error.status, this.serverCode)
  }
}

export interface VisionClientOptions {
  baseUrl?: string
  getToken: TokenGetter
  refreshAuth?: TokenGetter
  getTenant: TenantGetter
  onAuthFailure?: () => void
}

export function createVisionClient(opts: VisionClientOptions) {
  const baseUrl = (opts.baseUrl ?? env.VITE_VISION_BASE_URL).replace(/\/$/, '')

  async function request<S extends z.ZodTypeAny>(
    method: string,
    path: string,
    schema: S,
    init?: {
      body?: unknown
      query?: Record<string, string | string[] | number | undefined>
      idempotencyKey?: string
      headers?: Record<string, string>
    },
  ): Promise<z.infer<S>> {
    // FormData must go through untouched: the browser generates the multipart boundary and
    // setting Content-Type ourselves would omit it, so the server sees a body it cannot split.
    const isMultipart = init?.body instanceof FormData

    const url = buildUrl(baseUrl, path, init?.query)
    const res = await fetchWithAuthRetry(token => {
      const headers = new Headers({
        Accept: 'application/json',
        [TENANT_HEADER]: opts.getTenant(),
        [CORRELATION_HEADER]: correlationId(),
        [TRACEPARENT_HEADER]: newTraceparent(),
        Authorization: `Bearer ${token}`,
      })
      if (init?.body != null && !isMultipart) headers.set('Content-Type', 'application/json')
      if (init?.idempotencyKey) headers.set(IDEMPOTENCY_HEADER, init.idempotencyKey)
      for (const [name, value] of Object.entries(init?.headers ?? {})) headers.set(name, value)
      return fetch(url, {
        method,
        headers,
        body: bodyFor(init?.body, isMultipart),
        credentials: 'omit',
      })
    })

    if (!res.ok) throw await toApiError(res)

    const text = await res.text()
    const raw = text ? (JSON.parse(text) as unknown) : null
    const parsed = schema.safeParse(raw)
    if (!parsed.success) {
      if (IS_DEV) {
        console.warn(
          `[vision] response failed schema at ${method} ${path}`,
          parsed.error.flatten(),
          raw,
        )
      }
      throw new VisionApiError(
        200,
        `Malformed Vision response for ${method} ${path}`,
        parsed.error.flatten(),
      )
    }
    return parsed.data
  }

  return {
    listOrders(params: {
      tenantId?: string
      channel?: string | string[]
      channelCode?: string | null
      vendorCode?: string | string[] | null
      status?: OrderStatus[]
      bcSalesOrder?: BcSalesOrderFilter[]
      q?: string
      from?: string
      to?: string
      page?: number
      size?: number
      sort?: string
    }): Promise<OrderPage> {
      return request('GET', '/v1/orders', OrderPageSchema, {
        query: {
          channel: params.channel,
          // Directory slugs from GET /v1/channels and GET /v1/vendors. Vision
          // hotfix binds these onto canonical_orders.source_channel / vendor.
          channelCode: params.channelCode ?? undefined,
          vendorCode: params.vendorCode ?? undefined,
          status: params.status,
          // Rolled up per PO across its waves. Vision reads its own projection
          // for this — the queue never fans out to Heimdall.
          bcSalesOrder: params.bcSalesOrder,
          q: params.q,
          from: params.from,
          to: params.to,
          page: params.page,
          size: params.size,
          sort: params.sort,
        },
      })
    },
    listFulfillableOrders(params: {
      q?: string
      channel?: string
      vendor?: string
      cursor?: string
      size?: number
    }): Promise<FulfillableOrdersResponse> {
      return request(
        'GET',
        '/v1/bulk-warehouse-releases/fulfillable-orders',
        FulfillableOrdersResponseSchema,
        {
          query: {
            q: params.q,
            channel: params.channel,
            vendor: params.vendor,
            cursor: params.cursor,
            size: params.size,
          },
        },
      )
    },
    getOrdersSummary(params: {
      channel?: string | string[]
      channelCode?: string | null
      vendorCode?: string | string[] | null
      status?: OrderStatus[]
      bcSalesOrder?: BcSalesOrderFilter[]
      q?: string
      from?: string | null
      to?: string | null
    }): Promise<OrderSummary> {
      return request('GET', '/v1/orders/summary', OrderSummarySchema, {
        query: {
          channel: params.channel,
          channelCode: params.channelCode ?? undefined,
          vendorCode: params.vendorCode ?? undefined,
          status: params.status,
          bcSalesOrder: params.bcSalesOrder,
          q: params.q,
          from: params.from ?? undefined,
          to: params.to ?? undefined,
        },
      })
    },
    getOrder(externalOrderNumber: string): Promise<Order> {
      return request('GET', `/v1/orders/${encodeURIComponent(externalOrderNumber)}`, OrderSchema)
    },
    async cancelOrder(
      externalOrderNumber: string,
      body: OrderCancellationRequest,
      idempotencyKey: string = crypto.randomUUID(),
    ): Promise<OrderCancellationResponse> {
      const parsedBody = OrderCancellationRequestSchema.parse(body)
      try {
        return await request(
          'POST',
          `/v1/orders/${encodeURIComponent(externalOrderNumber)}/cancellations`,
          OrderCancellationResponseSchema,
          { body: parsedBody, idempotencyKey },
        )
      } catch (error) {
        if (error instanceof VisionApiError) throw new OrderCancellationApiError(error)
        throw error
      }
    },
    getAudit(externalOrderNumber: string): Promise<AuditTrail> {
      return request(
        'GET',
        `/v1/orders/${encodeURIComponent(externalOrderNumber)}/audit`,
        AuditTrailSchema,
      )
    },
    listItems(params: { channel: string; channelItemCodes: string[] }): Promise<ItemsResponse> {
      return request('GET', '/v1/items', ItemsResponseSchema, {
        query: {
          channel: params.channel,
          channelItemCodes: params.channelItemCodes.join(','),
        },
      })
    },
    /**
     * Get the latest validation run for the given order. Vision returns 404
     * when validation hasn't been executed yet — callers should treat that
     * as an "empty state", not an error.
     *
     * When Vision auto-reruns a stale run in-line (see W2's staleness
     * refresh), the response includes a `staleReason` field so the UI can
     * surface a small "Refreshed from Vision at …" banner.
     */
    getValidation(externalOrderNumber: string): Promise<ValidationRun> {
      return request(
        'GET',
        `/v1/orders/${encodeURIComponent(externalOrderNumber)}/validation`,
        ValidationRunSchema,
      )
    },
    /**
     * Admin-only: force Vision to re-run ITEM / CASEPACK / PRICE / HSN
     * against current product master. Response shape mirrors
     * {@link getValidation}. Safe on already-acked POs (chips only).
     *
     * Vision: `POST /v1/orders/{ext}/validation:rerun` (BoldfitAdmin, 202).
     */
    rerunValidation(externalOrderNumber: string): Promise<ValidationRun> {
      return request(
        'POST',
        `/v1/orders/${encodeURIComponent(externalOrderNumber)}/validation:rerun`,
        ValidationRunSchema,
        { idempotencyKey: crypto.randomUUID() },
      )
    },
    /**
     * List every active tenant Vision knows about — pulse-v2's tenant switcher
     * calls this once on boot and re-renders the store's `availableTenants`
     * from the result. See `vision/src/main/java/in/boldfit/vision/adapter/in/
     * http/TenantsController.java` for the server contract.
     */
    listTenants(): Promise<TenantsResponse> {
      return request('GET', '/v1/tenants', TenantsResponseSchema)
    },
    /**
     * List active channels for a tenant — backs the channel dropdown pulse-v2
     * renders above the Purchase Orders table. `tenantCode` is the business
     * slug pulse-v2 already carries in state (same value that goes on the
     * `X-Tenant-Id` header); the endpoint intentionally does NOT accept the
     * UUID id, to avoid an extra round-trip through `/v1/tenants` before every
     * dropdown render.
     */
    listChannels(params: { tenantCode: string }): Promise<ChannelsResponse> {
      return request('GET', '/v1/channels', ChannelsResponseSchema, {
        query: { tenantCode: params.tenantCode },
      })
    },
    /**
     * List active vendors for a tenant — backs the vendor dropdown. Same
     * `tenantCode` contract as {@link listChannels}.
     *
     * `channel` / `channelCode` narrow the list to vendors with at least one
     * PO on that channel, matched with the same predicate `GET /v1/orders`
     * uses. Omit both for the tenant-wide list. Vision ignores unknown query
     * params, so sending them before the server-side filter ships is safe —
     * the answer is simply unscoped.
     */
    listVendors(params: {
      tenantCode: string
      channel?: string | string[] | null
      channelCode?: string | null
    }): Promise<VendorsResponse> {
      return request('GET', '/v1/vendors', VendorsResponseSchema, {
        query: {
          tenantCode: params.tenantCode,
          channel: params.channel ?? undefined,
          channelCode: params.channelCode ?? undefined,
        },
      })
    },
    /** Effective Pulse identity derived from Shield roles. */
    getMe(): Promise<Me> {
      return request('GET', '/v1/me', MeSchema)
    },
    startAdminChannelSync(
      type: AdminChannelSyncType,
      channel: string,
    ): Promise<AdminChannelSyncStarted> {
      const path =
        type === 'MSD_PRICES'
          ? '/v1/admin/channel-syncs/msd-prices'
          : '/v1/admin/channel-syncs/vinculum'
      return request('POST', path, AdminChannelSyncStartedSchema, {
        body: { channel },
        idempotencyKey: crypto.randomUUID(),
      })
    },
    getAdminChannelSyncRun(runId: string): Promise<AdminChannelSyncRun> {
      return request(
        'GET',
        `/v1/admin/channel-syncs/runs/${encodeURIComponent(runId)}`,
        AdminChannelSyncRunSchema,
      )
    },
    getLatestAdminChannelSyncRun(
      type: AdminChannelSyncType,
      channel: string,
    ): Promise<AdminChannelSyncRun> {
      return request('GET', '/v1/admin/channel-syncs/latest', AdminChannelSyncRunSchema, {
        query: { type, channel },
      })
    },
    /**
     * Upload an MSL workbook. Heimdall upserts accepted rows — rows absent from
     * the sheet are left alone. Pass `dryRun: true` for a preview before apply.
     */
    uploadMsl(file: File, opts?: { dryRun?: boolean }): Promise<MslUploadResult> {
      const body = new FormData()
      body.append('file', file)
      body.append('dryRun', String(opts?.dryRun ?? false))
      return request('POST', '/v1/admin/msl/uploads', MslUploadResultSchema, { body })
    },
    /**
     * One page of current reserves. Backs the Excel export on the MSL page —
     * see `features/uploads/msl-export.ts`, which walks every page.
     *
     * Same Config Manager+ gate as {@link uploadMsl}.
     */
    listMsl(params?: {
      sku?: string
      regionCode?: string
      channel?: string
      /** `all` | `regional` | `channel` — ignored by older Heimdall. */
      tier?: string
      page?: number
      size?: number
    }): Promise<MslListResponse> {
      return request('GET', '/v1/admin/msl', MslListResponseSchema, {
        query: {
          sku: params?.sku,
          regionCode: params?.regionCode,
          channel: params?.channel,
          tier: params?.tier,
          page: params?.page,
          size: params?.size,
        },
      })
    },
    /**
     * Admin-only Amazon status reconciliation. Pulse always previews first; a confirmed restore
     * repeats the read live with `allowIncreases=true`.
     */
    readAmazonPoStatus(body: {
      poNumbers: string[]
      dryRun: boolean
      allowIncreases: boolean
    }): Promise<AmazonPoStatusReadResponse> {
      return request('POST', '/v1/admin/amazon/po-status/read', AmazonPoStatusReadResponseSchema, {
        body,
      })
    },
    /**
     * `lines` carries the Accepts only — Vision derives the Rejects from the latest
     * validation run. `expectedRejectedLines` is the caller's copy of that derivation:
     * Vision answers 409 rather than submitting when the two disagree. Omitting it
     * keeps the older unguarded behaviour.
     */
    createAcknowledgement(
      externalOrderNumber: string,
      body: {
        lines: { itemSequenceNumber: number; acknowledgedQuantity: number }[]
        expectedRejectedLines?: number[]
      },
      /**
       * Optional per-attempt key. Pulse bulk ack always sends one. Vision's sync
       * acknowledgement controller does not currently bind the header — sending it
       * is still safe and keeps the browser from inventing a second key on retry.
       */
      idempotencyKey: string = crypto.randomUUID(),
    ): Promise<AcknowledgementResponse> {
      return request(
        'POST',
        `/v1/orders/${encodeURIComponent(externalOrderNumber)}/acknowledgement`,
        AcknowledgementResponseSchema,
        {
          body,
          idempotencyKey,
        },
      )
    },
    /**
     * Every Vinculum hub. Pulse never calls Vinculum — Vision is the only
     * warehouse directory. No class / channel query filter.
     * Vision OpenAPI: `{ items: [{ code, name?, inferredClass }] }`.
     */
    listWarehouses(): Promise<WarehousesResponse> {
      return request('GET', '/v1/warehouses', WarehousesResponseSchema)
    },
    /**
     * ATP for each PO line, split by warehouse. 404 / empty means the
     * Vinculum feed is pending — callers show `—`.
     */
    getOrderInventory(externalOrderNumber: string): Promise<OrderInventory> {
      return request(
        'GET',
        `/v1/orders/${encodeURIComponent(externalOrderNumber)}/inventory`,
        OrderInventorySchema,
      )
    },
    /**
     * Live ATP for one PO, pushed as Vision learns of stock movement.
     *
     * Strictly additive to `getOrderInventory`, which stays the source of truth: the stream
     * carries the identical payload and only exists to deliver later versions of it. Vision's
     * fan-out is at-most-once and every deploy severs every stream, so `onReconnect` is where
     * correctness is recovered — refetch there rather than assuming the gap was empty.
     *
     * Uses `fetchEventSource`, not the browser's `EventSource`, because the native one cannot
     * set request headers and Vision reads both the bearer token and `X-Tenant-Id` from them.
     * The token is resolved per connection attempt, so a reconnect after Vision closes the
     * stream at its lifetime cap picks up a freshly minted one.
     *
     * @returns an abort function; call it on unmount.
     */
    streamOrderInventory(
      externalOrderNumber: string,
      handlers: {
        onInventory: (inventory: OrderInventory) => void
        onReconnect?: () => void
        onFatal?: (error: unknown) => void
      },
    ): () => void {
      const controller = new AbortController()
      // `fetchEventSource` calls onopen again on every reconnect, so the first open has to be
      // distinguished from the rest — otherwise mounting the stream would immediately trigger
      // the resync path and duplicate the REST fetch the caller just did.
      let opened = false

      void fetchEventSource(
        buildUrl(baseUrl, `/v1/orders/${encodeURIComponent(externalOrderNumber)}/inventory/stream`),
        {
          signal: controller.signal,
          // Without this the browser tears the connection down when the tab is hidden and does
          // not restore it, so an operator returning to a background tab would sit on a frozen
          // grid with no indication it had stopped updating.
          openWhenHidden: true,
          headers: {
            Accept: 'text/event-stream',
            [TENANT_HEADER]: opts.getTenant(),
          },
          // Auth is injected here rather than in `headers` because `headers` is captured once,
          // while this runs on every connection attempt. Vision closes each stream at its
          // lifetime cap precisely so the client comes back with a fresh token, and a token
          // frozen at first mount would defeat that — the reconnect would present a credential
          // already past its 60-90 minute life.
          fetch: async (input, init) => {
            const token = await opts.getToken()
            const headers = new Headers(init?.headers)
            headers.set('Authorization', `Bearer ${token}`)
            headers.set(CORRELATION_HEADER, correlationId())
            headers.set(TRACEPARENT_HEADER, newTraceparent())
            return window.fetch(input, { ...init, headers })
          },
          async onopen(response) {
            if (response.ok) {
              if (opened) handlers.onReconnect?.()
              opened = true
              return
            }
            // 401/403 will not fix themselves by retrying, and fetchEventSource retries anything
            // that does not throw. Throwing from onopen is how you stop it.
            throw new VisionApiError(
              response.status,
              `Inventory stream refused for ${externalOrderNumber}`,
              undefined,
              response.headers.get(CORRELATION_HEADER) ?? undefined,
            )
          },
          onmessage(event) {
            if (event.event !== 'inventory' || !event.data) return
            const parsed = OrderInventorySchema.safeParse(JSON.parse(event.data) as unknown)
            if (!parsed.success) {
              // Same contract as the REST payload, so a mismatch is a Vision drift worth seeing
              // in dev. Dropping the frame leaves the last good grid on screen.
              if (IS_DEV) {
                console.warn(
                  '[vision] inventory stream frame failed schema',
                  parsed.error.flatten(),
                )
              }
              return
            }
            handlers.onInventory(parsed.data)
          },
          onerror(error) {
            if (error instanceof VisionApiError) {
              handlers.onFatal?.(error)
              throw error // stop retrying
            }
            // Anything else is a transport blip — a deploy, a laptop lid, the ALB culling an
            // idle connection. Returning lets the library back off and retry.
          },
        },
      )

      return () => controller.abort()
    },
    createWarehouseRelease(
      externalOrderNumber: string,
      body: {
        lines: WarehouseReleaseLine[]
      },
    ): Promise<WarehouseReleaseResponse> {
      return request(
        'POST',
        `/v1/orders/${encodeURIComponent(externalOrderNumber)}/warehouse-releases`,
        WarehouseReleaseResponseSchema,
        {
          body,
        },
      )
    },
    submitBulkWarehouseRelease(
      body: BulkWarehouseReleaseRequest,
      idempotencyKey: string,
    ): Promise<BulkWarehouseReleaseResponse> {
      return request('POST', '/v1/bulk-warehouse-releases', BulkWarehouseReleaseResponseSchema, {
        body,
        idempotencyKey,
      })
    },
    listBulkWarehouseReleases(params: {
      page?: number
      size?: number
    }): Promise<BulkWarehouseReleaseHistory> {
      return request('GET', '/v1/bulk-warehouse-releases', BulkWarehouseReleaseHistorySchema, {
        query: {
          page: params.page,
          size: params.size,
        },
      })
    },
    getBulkWarehouseRelease(batchId: string): Promise<BulkWarehouseReleaseResponse> {
      return request(
        'GET',
        `/v1/bulk-warehouse-releases/${encodeURIComponent(batchId)}`,
        BulkWarehouseReleaseResponseSchema,
      )
    },
    /**
     * `channel` repeats, same as `status` and the same key `GET /v1/orders`
     * already uses. Each value is a marketplace family stamp matched to
     * `sourceChannel`. `amazon` and `amazon_now` are different families.
     *
     * The JIT list contract Pulse shipped against accepts `q`, `status`,
     * `page`, and `size` only. Vision does not document `channel` on
     * `GET /v1/jit-purchase-orders` yet. Pulse sends it so the queue can
     * narrow server-side; until Vision binds the parameter onto
     * `sourceChannel`, an unknown query param is ignored and production
     * pages stay unfiltered. Demo mode filters its in-memory fixtures.
     */
    listJitPurchaseOrders(params: {
      q?: string
      channel?: string[]
      vendorCode?: string[]
      status?: JitWorkItemStatus[]
      from?: string
      to?: string
      sort?: string
      page?: number
      size?: number
    }): Promise<JitPurchaseOrderPage> {
      return request('GET', '/v1/jit-purchase-orders', JitPurchaseOrderPageSchema, {
        query: {
          q: params.q,
          channel: params.channel,
          vendorCode: params.vendorCode,
          status: params.status,
          from: params.from,
          to: params.to,
          sort: params.sort,
          page: params.page,
          size: params.size,
        },
      })
    },
    getJitPurchaseOrder(workItemId: string): Promise<JitPurchaseOrderDetail> {
      return request(
        'GET',
        `/v1/jit-purchase-orders/${encodeURIComponent(workItemId)}`,
        JitPurchaseOrderDetailSchema,
      )
    },
    submitJitPurchaseOrder(
      workItemId: string,
      body: JitSubmissionRequest,
      idempotencyKey: string,
      version: number,
    ): Promise<JitSubmissionResult> {
      return request(
        'POST',
        `/v1/jit-purchase-orders/${encodeURIComponent(workItemId)}/submissions`,
        JitSubmissionResultSchema,
        {
          body,
          idempotencyKey,
          headers: { 'If-Match': `"${version}"` },
        },
      )
    },
    /**
     * Bulk JIT submit — `perSource` (N Vulcan plans) or `mergeByEan` (one plan).
     * Vision owns plan creation; Pulse only posts the operator sheet.
     */
    submitJitBulkPurchaseOrders(
      body: JitBulkSubmissionRequest,
      idempotencyKey: string,
    ): Promise<JitBulkSubmissionResult> {
      return request(
        'POST',
        '/v1/jit-purchase-orders/bulk-submissions',
        JitBulkSubmissionResultSchema,
        { body, idempotencyKey },
      )
    },
    /**
     * Every Vinculum Sales Order across all POs — the SO-centric queue.
     *
     * One row per SO rather than per PO, because a PO fans out to N waves and
     * the question an operator actually asks ("what has stopped moving") is
     * about a wave. `sort` defaults to `-timeInStage` server-side for the same
     * reason.
     *
     * `warehouseStatus`, `bcStatus`, `channel`, `vendorCode` and
     * `pickupLocation` all repeat; `buildUrl` appends arrays, so passing one
     * sends `?channel=a&channel=b`. `from` / `to` filter on PO date and Vision
     * caps the window at 180 days.
     */
    listSalesOrders(params: {
      q?: string
      warehouseStatus?: SalesOrderWarehouseFilter[]
      bcStatus?: SalesOrderBcFilter[]
      exceptionsOnly?: boolean
      channel?: string[]
      vendorCode?: string[]
      pickupLocation?: string[]
      stuckOverHours?: number
      from?: string
      to?: string
      page?: number
      size?: number
      sort?: SalesOrderSort
    }): Promise<PagedSalesOrderResponse> {
      return request('GET', '/v1/sales-orders', PagedSalesOrderResponseSchema, {
        query: {
          q: params.q,
          warehouseStatus: params.warehouseStatus,
          bcStatus: params.bcStatus,
          // Only sent when on. `buildUrl` drops undefined, and `false` would
          // otherwise go on the wire as a filter nobody asked for.
          exceptionsOnly: params.exceptionsOnly ? 'true' : undefined,
          channel: params.channel,
          vendorCode: params.vendorCode,
          pickupLocation: params.pickupLocation,
          stuckOverHours: params.stuckOverHours,
          from: params.from,
          to: params.to,
          page: params.page,
          size: params.size,
          sort: params.sort,
        },
      })
    },
    /** One Sales Order's full journey: the list facts plus `events` and `lines`. */
    getSalesOrder(soNumber: string): Promise<SalesOrderDetail> {
      return request(
        'GET',
        `/v1/sales-orders/${encodeURIComponent(soNumber)}`,
        SalesOrderDetailSchema,
      )
    },
    /**
     * Ask Heimdall to cancel a Vinculum SO. 200 / 409 / Heimdall-shaped 502
     * share {@link CancelSalesOrderResponseSchema}. 403 / 404 / 422 and a
     * transport 502 are RFC 7807 via {@link VisionApiError}.
     */
    cancelSalesOrder(
      soNumber: string,
      body: CancelSalesOrderRequest,
    ): Promise<CancelSalesOrderResponse> {
      return cancelRequest(soNumber, body)
    },
    /**
     * Admin-only: pull Vinculum `/v2/order/status` through Heimdall and apply
     * this wave. 403 / 404 / 502 are RFC 7807 via {@link VisionApiError}.
     */
    refreshWarehouseStatus(soNumber: string): Promise<RefreshWarehouseStatusResponse> {
      return request(
        'POST',
        `/v1/sales-orders/${encodeURIComponent(soNumber)}/warehouse-status:refresh`,
        RefreshWarehouseStatusResponseSchema,
      )
    },
    listInventoryReports(): Promise<InventoryReportCatalog> {
      return request('GET', '/v1/reports', InventoryReportCatalogSchema)
    },
    /**
     * Stream one report file. Returns the blob rather than triggering the
     * save dialog so the caller can name the file from Content-Disposition.
     * Does not parse the body as JSON.
     */
    async downloadInventoryReport(
      reportId: string,
      query?: { channel?: string; from?: string; to?: string },
    ): Promise<{ blob: Blob; filename: string }> {
      const url = buildUrl(baseUrl, `/v1/reports/${encodeURIComponent(reportId)}`, query)
      const res = await fetchWithAuthRetry(token => {
        const headers = new Headers({
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv',
          [TENANT_HEADER]: opts.getTenant(),
          [CORRELATION_HEADER]: correlationId(),
          Authorization: `Bearer ${token}`,
        })
        return fetch(url, {
          method: 'GET',
          headers,
          credentials: 'omit',
        })
      })
      if (!res.ok) throw await toApiError(res)
      return {
        blob: await res.blob(),
        filename:
          filenameFromContentDisposition(res.headers.get('content-disposition')) ??
          fallbackReportFilename(reportId, res.headers.get('content-type')),
      }
    },
  }

  async function cancelRequest(
    soNumber: string,
    body: CancelSalesOrderRequest,
  ): Promise<CancelSalesOrderResponse> {
    const path = `/v1/sales-orders/${encodeURIComponent(soNumber)}/cancel`
    const res = await fetchWithAuthRetry(token => {
      const headers = new Headers({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [TENANT_HEADER]: opts.getTenant(),
        [CORRELATION_HEADER]: correlationId(),
        Authorization: `Bearer ${token}`,
      })
      return fetch(buildUrl(baseUrl, path), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'omit',
      })
    })
    const text = await res.text()
    let raw: unknown = null
    if (text) {
      try {
        raw = JSON.parse(text) as unknown
      } catch {
        throw new VisionApiError(res.status, text, null, correlationFrom(res))
      }
    }
    const parsed = CancelSalesOrderResponseSchema.safeParse(raw)
    if (parsed.success && (res.ok || res.status === 409 || res.status === 502)) {
      return parsed.data
    }
    if (IS_DEV && !parsed.success && (res.ok || res.status === 409 || res.status === 502)) {
      console.warn(`[vision] response failed schema at POST ${path}`, parsed.error.flatten(), raw)
    }
    throw apiErrorFromBody(res, raw, text)
  }

  async function fetchWithAuthRetry(run: (token: string) => Promise<Response>): Promise<Response> {
    const first = await run(await opts.getToken())
    if (!opts.refreshAuth || !isAuthRetryStatus(first.status)) return first

    const second = await run(await opts.refreshAuth())
    if (second.status === 401) opts.onAuthFailure?.()
    return second
  }
}

export type VisionClient = ReturnType<typeof createVisionClient>

/** JSON-encodes a body, but hands FormData to fetch as-is so the boundary survives. */
function bodyFor(body: unknown, isMultipart: boolean): BodyInit | undefined {
  if (body == null) return undefined
  return isMultipart ? (body as FormData) : JSON.stringify(body)
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, string | string[] | number | undefined>,
): string {
  const url = new URL(baseUrl + path)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value == null || value === '') continue
      if (Array.isArray(value)) {
        for (const item of value) if (item !== '') url.searchParams.append(key, String(item))
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }
  return url.toString()
}

async function toApiError(res: Response): Promise<VisionApiError> {
  const text = await res.text().catch(() => '')
  let raw: unknown = null
  if (text) {
    try {
      raw = JSON.parse(text) as unknown
    } catch {
      return new VisionApiError(res.status, text, null, correlationFrom(res))
    }
  }
  return apiErrorFromBody(res, raw, text)
}

function apiErrorFromBody(res: Response, raw: unknown, text: string): VisionApiError {
  const correlation = correlationFrom(res)
  if (!text) {
    return new VisionApiError(res.status, `${res.status} ${res.statusText}`, null, correlation)
  }
  const problem = ProblemSchema.safeParse(raw)
  if (problem.success) {
    return new VisionApiError(
      res.status,
      problem.data.detail || problem.data.title,
      problem.data,
      problem.data.correlationId ?? correlation,
    )
  }
  return new VisionApiError(res.status, `${res.status} ${res.statusText}`, raw, correlation)
}

function correlationFrom(res: Response): string | undefined {
  return res.headers.get(CORRELATION_HEADER) ?? res.headers.get('x-correlation-id') ?? undefined
}

function correlationId(): string {
  return crypto.randomUUID()
}

function isAuthRetryStatus(status: number): boolean {
  return status === 401 || status === 403
}

function problemCode(problem: unknown): string | undefined {
  if (!problem || typeof problem !== 'object' || !('code' in problem)) return undefined
  const code = (problem as { code?: unknown }).code
  return typeof code === 'string' ? code : undefined
}

function cancellationErrorCode(
  status: number,
  serverCode: string | undefined,
): OrderCancellationErrorCode {
  switch (serverCode) {
    case 'NOT_ACKNOWLEDGED':
    case 'NOTHING_TO_CANCEL':
    case 'QUANTITY_EXCEEDS_CANCELLABLE':
    case 'ALREADY_CANCELLED':
    case 'IDEMPOTENCY_CONFLICT':
    case 'TERMINAL_ORDER':
    case 'LINE_NOT_FOUND':
    case 'PO_CANCELLATION_DISABLED':
      return serverCode
    default:
      if (status === 403) return 'FORBIDDEN'
      if (status === 409) return 'CONFLICT'
      return 'UNKNOWN'
  }
}

function fallbackReportFilename(reportId: string, contentType: string | null): string {
  if (contentType?.includes('spreadsheetml')) {
    return 'pulse-inventory.xlsx'
  }
  return `pulse-inventory-${reportId}.csv`
}

/** `attachment; filename="pulse-inventory.xlsx"` — quoted or bare. */
export function filenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined
  const utf8 = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header)
  if (utf8?.[1]) return decodeURIComponent(utf8[1].replaceAll('"', '').trim())
  const plain = /filename=([^;]+)/i.exec(header)
  if (plain?.[1]) return plain[1].replaceAll('"', '').trim()
  return undefined
}
